import { z } from "zod"

/**
 * Auth şemaları. Hem API response parsing hem form validasyonu için tek kaynak.
 * Backend gerçek response şekli geldiğinde (npm run gen:api) bu şemalar tipleri
 * ile karşılaştırılıp refine edilir.
 */

export const roleSchema = z.enum([
  "Administrator",
  "Partner",
  "Customer",
  "MobilUser",
  "TechnicalServiceUser",
  "DashboardUser",
])
export type Role = z.infer<typeof roleSchema>

// Panel discriminator. Tek admin SPA içinden iki ürünü (Hems + CNI) yönetiyoruz.
// Login sonrası user.panels yetkili paneller listesini taşır; tek panelli user
// otomatik o panele yönlenir, iki panelliye `/select-panel` gösterilir.
export const panelSchema = z.enum(["hems", "cni"])
export type Panel = z.infer<typeof panelSchema>

export const PANEL_VALUES: readonly Panel[] = panelSchema.options

// "user@host" — TLD zorunlu değil (dev seed `administrator@localhost` kabul edilsin).
// Zod v3 `.email()` RFC'nin sıkı alt kümesini kullanır ve `@localhost` reddeder.
const emailLike = z
  .string()
  .min(1)
  .regex(/^[^\s@]+@[^\s@]+$/, "Geçerli bir e-posta girin")

export const loginInputSchema = z.object({
  email: emailLike,
  password: z.string().min(6),
})
export type LoginInput = z.infer<typeof loginInputSchema>

export const loginResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
})
export type LoginResponse = z.infer<typeof loginResponseSchema>

export const refreshInputSchema = z.object({
  refreshToken: z.string().min(1),
})
export type RefreshInput = z.infer<typeof refreshInputSchema>

export const userProfileSchema = z.object({
  id: z.string().min(1),
  email: emailLike,
  name: z.string().min(1),
  roles: z.array(roleSchema),
  panels: z.array(panelSchema).default([]),
  partnerId: z.string().nullable().optional(),
  partnerName: z.string().nullable().optional(),
  // ADR #025/#026 — org hiyerarşisi scope claim'leri. Org login'lerinde backend
  // doldurur; admin'de null. `useCurrentScope()` bunlardan subtree scope türetir.
  orgId: z.string().nullable().optional(),
  orgPath: z.string().nullable().optional(),
})
export type UserProfile = z.infer<typeof userProfileSchema>

// Backend /Auth/user-status endpoint'inin tam şekli kesinleşince buraya refine.
// Şimdilik { user: ... } sarmalayıcısını destekleyelim, direkt döndürseler de.
export const userStatusResponseSchema = z.union([
  z.object({ user: userProfileSchema }),
  userProfileSchema,
])
export type UserStatusResponse = z.infer<typeof userStatusResponseSchema>

// Hems-Backend /api/SuperAdmin/auth/me response shape (SuperAdminMeDto).
// getCurrentUser() bu endpoint'i çağırıp UserProfile'a map ediyor (api.ts'te).
// ADR #020: partnerId/partnerName opsiyonel alanlar — partner login'lerinde
// backend bu iki alanı doldurarak döner.
export const superAdminMeDtoSchema = z.object({
  id: z.string().min(1),
  email: emailLike,
  firstName: z.string().nullable().optional(),
  surname: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  roles: z.array(z.string()),
  panels: z.array(z.string()).optional().default([]),
  partnerId: z.string().nullable().optional(),
  partnerName: z.string().nullable().optional(),
  // ADR #025/#026 — org scope claim'leri (org login'lerinde dolu).
  orgId: z.string().nullable().optional(),
  orgPath: z.string().nullable().optional(),
})
export type SuperAdminMeDto = z.infer<typeof superAdminMeDtoSchema>
